import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';

import config from '../../config';
import { userLocation } from '../../util/maps';
import { loadMapsLib } from '../../util/mapsLib';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 500,
  country: 2000,
  region: 2000,
  postcode: 2000,
  district: 2000,
  place: 2000,
  locality: 2000,
  neighborhood: 2000,
  poi: 2000,
  'poi.landmark': 2000,
};

const sortingOrder = config.maps.search.sortingOrder;
const compareTypes = (key, order = 'asc') => {
  return (a, b) => {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) return 0;

    const first =
      a[key][0].toLowerCase() in sortingOrder ? sortingOrder[a[key][0]] : Number.MAX_SAFE_INTEGER;
    const second =
      b[key][0].toLowerCase() in sortingOrder ? sortingOrder[b[key][0]] : Number.MAX_SAFE_INTEGER;

    let result = 0;
    if (first < second) result = -1;
    else if (first > second) result = 1;
    return order === 'desc' ? ~result : result;
  };
};

// Mapbox' own value in LngLat.toBounds(), kept identical so the generated
// bounding boxes do not shift.
const EARTH_CIRCUMFERENCE_AT_EQUATOR_M = 40075017;

/**
 * Square bounding box of `distance` meters around a point.
 *
 * This is a direct port of mapbox-gl's `LngLat.toBounds()`. It is deliberately
 * dependency-free: `locationBounds` runs synchronously in the "use my current
 * location" handlers of the top bar, the hero search form and the search page,
 * which must keep working before mapbox-gl has been downloaded.
 */
export const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const latAccuracy = (360 * distance) / EARTH_CIRCUMFERENCE_AT_EQUATOR_M;
  const lngAccuracy = latAccuracy / Math.cos((Math.PI / 180) * latlng.lat);

  return new SDKLatLngBounds(
    new SDKLatLng(latlng.lat + latAccuracy, latlng.lng + lngAccuracy),
    new SDKLatLng(latlng.lat - latAccuracy, latlng.lng - lngAccuracy)
  );
};

/**
 * Center of a Mapbox-style bounds tuple `[minX, minY, maxX, maxY]`
 * (i.e. `[west, south, east, north]`), as `{ lat, lng }`.
 *
 * Equivalent to `new mapboxgl.LngLatBounds(sw, ne).getCenter()`, minus the
 * dependency: the region predictions are built while the user types in the
 * search field, which must not require mapbox-gl to have loaded. Entries may
 * be strings (they come from `String.split(',')`), so they are coerced the way
 * mapbox-gl's LngLat constructor does.
 *
 * @param {Array<string|number>} bounds
 * @returns {{lat: number, lng: number}}
 */
export const boundsCenter = (bounds) => ({
  lng: (Number(bounds[0]) + Number(bounds[2])) / 2,
  lat: (Number(bounds[1]) + Number(bounds[3])) / 2,
});

export const placeOrigin = (prediction) => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

export const placeBounds = (prediction) => {
  if (prediction) {
    if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
      // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
      return new SDKLatLngBounds(
        new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
        new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
      );
    } else {
      // If bounds are not available, generate them around the origin

      // Resolve bounds distance based on place type
      const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

      const distance =
        (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

      return locationBounds(placeOrigin(prediction), distance);
    }
  }
  return null;
};

export const GeocoderAttribution = () => null;

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  /**
   * Resolves the Mapbox SDK client, downloading the Mapbox libraries first if
   * they are not on the page yet.
   *
   * Async because mapbox-sdk is no longer loaded on page load — see
   * src/util/mapsLib.js. Callers warm it up on focus of the search field so
   * this normally resolves immediately.
   *
   * @returns {Promise<Object>}
   */
  async getClient() {
    if (this._client) {
      return this._client;
    }
    await loadMapsLib();
    const libLoaded = typeof window !== 'undefined' && window.mapboxgl && window.mapboxSdk;
    if (!libLoaded) {
      throw new Error('Mapbox libraries are required for GeocoderMapbox');
    }
    this._client = window.mapboxSdk({
      accessToken: window.mapboxgl.accessToken,
    });
    return this._client;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {string} search query for place names
   *
   * @returns {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  async getPlacePredictionsForSearch(search, locale) {
    const limitCountriesMaybe = config.maps.search.countryLimit
      ? { countries: config.maps.search.countryLimit }
      : {};

    const client = await this.getClient();
    return client.geocoding
      .forwardGeocode({
        query: search,
        limit: 5,
        types: [
          'country',
          'region',
          'postcode',
          'district',
          'place',
          //'locality' ,
          //'neighborhood' ,
          //'address' ,
          //'poi',
        ],
        ...limitCountriesMaybe,
        language: [locale],
      })
      .send()
      .then((response) => {
        response.body.features = response.body.features.filter((feature) => {
          if (feature['place_type'].includes('poi')) {
            return config.maps.search.poiCategories.some(
              (v) => feature.properties.category && feature.properties.category.includes(v)
            );
          }
          return true;
        });
        response.body.features = response.body.features.sort(compareTypes('place_type'));
        return {
          search,
          predictions: response.body.features,
        };
      });
  }

  async getPlacePredictionsForAddress(search, locale) {
    const limitCountriesMaybe = config.maps.search.countryLimit
      ? { countries: config.maps.search.countryLimit }
      : {};

    const client = await this.getClient();
    return client.geocoding
      .forwardGeocode({
        query: search,
        limit: 5,
        types: [
          // "country" ,
          // "region",
          // "postcode" ,
          // "district" ,
          'place',
          'locality',
          'neighborhood',
          'address',
          'poi',
        ],
        ...limitCountriesMaybe,
        language: [locale],
      })
      .send()
      .then((response) => {
        return {
          search,
          predictions: response.body.features,
        };
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {object} prediction selected prediction object
   *
   * @returns {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then((latlng) => {
        return {
          address: '',
          origin: latlng,
          bounds: locationBounds(latlng, config.maps.search.currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
    });
  }

  async getLocationForGeoLocation(location) {
    const client = await this.getClient();
    return client.geocoding
      .reverseGeocode({
        query: [location.lng, location.lat],
        types: [
          'country',
          // "region" ,
          // "postcode" ,
          // "district" ,
          'place',
          // "locality" ,
          // "neighborhood" ,
          'address',
          // "poi"
        ],
        language: [config.locale],
      })
      .send()
      .then((response) => {
        return response.body.features;
      });
  }
}

export default GeocoderMapbox;
